import React, { useEffect, useState } from 'react';
import { FaHourglassHalf, FaUserCircle } from 'react-icons/fa';
import styled from 'styled-components';
import styles from './Dashboard.module.css'
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import RemoveShoppingCartOutlinedIcon from '@mui/icons-material/RemoveShoppingCartOutlined';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import SpeakerNotesIcon from '@mui/icons-material/SpeakerNotes';
import TodayIcon from "@mui/icons-material/Today";
import EventIcon from "@mui/icons-material/Event";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import HouseIcon from '@mui/icons-material/House';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import GetAppIcon from '@mui/icons-material/GetApp';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SettingsIcon from '@mui/icons-material/Settings';
import { Table, message, Input, Button, Select, Badge } from "antd";
import { motion, AnimatePresence } from 'framer-motion';
import Logout from './Logout';
import APIURL from '../api/apiClient';

const { Option } = Select;

// Enhanced styled components with sky blue color combination
const DashboardContainer = styled.div`
  background: linear-gradient(135deg, #e0f7fa 0%, #f0f9ff 40%, #e6f3ff 100%);
  min-height: 100vh;
  padding: 0;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 80%, rgba(0, 188, 212, 0.08) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(3, 169, 244, 0.06) 0%, transparent 50%),
      radial-gradient(circle at 40% 40%, rgba(0, 172, 193, 0.04) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const HeaderContainer = styled.header`
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(15px);
  padding: 16px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 20px rgba(0, 188, 212, 0.1);
  border-bottom: 1px solid rgba(0, 188, 212, 0.1);
  position: sticky;
  top: 0;
  z-index: 100;
  margin-bottom: 24px;
`;

const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const UserProfileHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const UserInfoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: linear-gradient(135deg, #e0f7fa 0%, #b3e5fc 100%);
  padding: 10px 16px;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 188, 212, 0.15);
  border: 1px solid rgba(0, 188, 212, 0.2);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.3), transparent);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 25px rgba(0, 188, 212, 0.2);
    
    &::before {
      opacity: 1;
    }
  }
`;

const UserAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #00acc1, #0097a7);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 16px;
  box-shadow: 0 2px 10px rgba(0, 172, 193, 0.3);
`;

const NotificationButton = styled.button`
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(0, 188, 212, 0.2);
  border-radius: 10px;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(5px);
  
  &:hover {
    background: rgba(255, 255, 255, 0.95);
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(0, 188, 212, 0.15);
    border-color: rgba(0, 188, 212, 0.3);
  }
  
  svg {
    color: #00838f;
    font-size: 20px;
  }
`;

const ContentContainer = styled.div`
  padding: 0 24px 24px;
  position: relative;
  z-index: 1;
`;

const HeaderSection = styled.div`
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(15px);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 4px 20px rgba(0, 188, 212, 0.08);
  border: 1px solid rgba(0, 188, 212, 0.1);
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
`;

const StatusGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 16px;
  margin-bottom: 32px;
`;

const StyledCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(15px);
  border-radius: 16px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 20px rgba(0, 188, 212, 0.08);
  border: 1px solid rgba(0, 188, 212, 0.1);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: linear-gradient(135deg, ${props => props.accentColor || '#00bcd4'}, ${props => props.accentColor + '80' || '#00bcd480'});
    transition: width 0.3s ease;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background: linear-gradient(135deg, transparent 0%, rgba(0, 188, 212, 0.02) 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover::before {
    width: 6px;
  }

  &:hover::after {
    opacity: 1;
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 30px rgba(0, 188, 212, 0.15);
    border-color: rgba(0, 188, 212, 0.2);
    background: rgba(255, 255, 255, 0.95);
  }
`;

const StatusCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(15px);
  border-radius: 12px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 3px 15px rgba(0, 188, 212, 0.06);
  border: 1px solid rgba(0, 188, 212, 0.08);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 3px;
    height: 100%;
    background: linear-gradient(135deg, ${props => props.accentColor || '#00bcd4'}, ${props => props.accentColor + '80' || '#00bcd480'});
    transition: width 0.3s ease;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background: linear-gradient(135deg, transparent 0%, ${props => props.accentColor + '05' || 'rgba(0, 188, 212, 0.05)'} 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover::before {
    width: 5px;
  }

  &:hover::after {
    opacity: 1;
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 25px rgba(0, 188, 212, 0.12);
    background: rgba(255, 255, 255, 0.95);
  }
`;

const TableContainer = styled(motion.div)`
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(15px);
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 188, 212, 0.08);
  border: 1px solid rgba(0, 188, 212, 0.1);
  overflow: hidden;
`;

const TableHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 16px;
`;

const SearchContainer = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
`;

const ActionButton = styled(Button)`
  border-radius: 8px;
  border: 1px solid rgba(0, 188, 212, 0.2);
  transition: all 0.3s ease;
  height: 36px;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(5px);

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 188, 212, 0.15);
    border-color: rgba(0, 188, 212, 0.3);
    background: rgba(255, 255, 255, 0.95);
  }
`;

const IconContainer = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.bgColor || '#f1f5f9'};
  margin-bottom: 12px;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.3), transparent);
    border-radius: 12px;
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  ${StyledCard}:hover &::after,
  ${StatusCard}:hover &::after {
    opacity: 1;
  }
`;

const Dashboard = () => {
  const [userData, setUserData] = useState({
    username: localStorage.getItem("username") || "User",
    role: localStorage.getItem("userRole") || "Employee",
  });

  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');


  // Counts state
  const [todayOrderCount, setTodayOrderCount] = useState(0);
  const [todayOrders, setTodayOrders] = useState('');
  const [totalOrderCount, setTotalOrderCount] = useState(0);
  const [totalOrders, setTotalOrders] = useState('');
  const [yesterdayOrdercount, setYesterdayOrderCount] = useState(0);
  const [yesterdayOrders, setYesterdayOrders] = useState('');
  const [processingOrderCount, setProcessingOrderCount] = useState(0);
  const [processingOrders, setProcessingOrders] = useState("");
  const [pendingOrderCount, setPendingOrderCount] = useState(0);
  const [pendingOrders, setPendingOrders] = useState('');
  const [shippedOrderCount, setShippedOrderCount] = useState(0);
  const [shippedOrders, setShippedOrders] = useState('');
  const [deliveredOrderCount, setDeliveredOrderCount] = useState(0);
  const [deliveredOrders, setDeliveredOrders] = useState('');
  const [canceledOrderCount, setCanceledOrderCount] = useState(0);
  const [canceledOrders, setCanceledOrders] = useState('');

  // UI state
  const [selectedTable, setSelectedTable] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);


  useEffect(() => {
    const handleStorageChange = () => {
      setUserData({
        username: localStorage.getItem("username") || "User",
        role: localStorage.getItem("userRole") || "Employee",
      });
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);


  // Calculate pagination values
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentOrders = filteredOrders.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);
  const totalItems = filteredOrders.length;

  // Animation variants
  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
    hover: { y: -4, scale: 1.02 }
  };

  const tableVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
  };

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const endpoints = [
          "fetchtotalorders.php",
          "fetchtodayorders.php",
          "fetchyesterdayorders.php",
          "fetchprocessingorders.php",
          "fetchpendingorders.php",
          "fetchshippedorders.php",
          "fetchdeliveredorders.php",
          "fetchcanceledorders.php"
        ];

        const responses = await Promise.all(
          endpoints.map((endpoint) => fetch(`${APIURL.baseURL}/${endpoint}`))
        );

        // Check if any request failed
        if (responses.some((res) => !res.ok)) {
          throw new Error("Failed to fetch data");
        }

        const [
          totalData,
          todayData,
          yesterdayData,
          processingData,
          pendingData,
          shippedData,
          deliveredData,
          canceledData
        ] = await Promise.all(responses.map((res) => res.json()));

        setTotalOrderCount(totalData.count || 0);
        setTotalOrders(totalData.orders || []);

        setTodayOrderCount(todayData.today_count || 0);
        setTodayOrders(todayData.orders || []);

        setYesterdayOrderCount(yesterdayData.yesterday_count || 0);
        setYesterdayOrders(yesterdayData.orders || []);

        setProcessingOrderCount(processingData.processing_count || 0);
        setProcessingOrders(processingData.orders || []);

        setPendingOrderCount(pendingData.pending_count || 0);
        setPendingOrders(pendingData.orders || []);

        setShippedOrderCount(shippedData.shipped_count || 0);
        setShippedOrders(shippedData.orders || []);

        setDeliveredOrderCount(deliveredData.delivered_count || 0);
        setDeliveredOrders(deliveredData.orders || []);

        setCanceledOrderCount(canceledData.canceled_count || 0);
        setCanceledOrders(canceledData.orders || []);
      } catch (err) {
        console.error("Error fetching orders:", err);
        setError(err.message);
        message.error("Failed to fetch dashboard data");
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, []);

  // Filter orders based on search and status
  useEffect(() => {
    let filtered = orders;

    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(order =>
        String(order.user_name || '').toLowerCase().includes(lowerSearch) ||
        String(order.order_id || '').toLowerCase().includes(lowerSearch) ||
        String(order.payment_method || '').toLowerCase().includes(lowerSearch)
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(order =>
        String(order.order_status || '').toLowerCase() === statusFilter.toLowerCase()
      );
    }

    setFilteredOrders(filtered);
    setCurrentPage(1); // Reset to first page when filtering
  }, [orders, searchTerm, statusFilter]);


  const handleCardClick = async (target) => {
    console.log(`${target} Clicked`);
    setSelectedTable(target);
    setLoading(true);

    const endpointsMap = {
      "Total Orders": "fetchtotalorders.php",
      "Today Orders": "fetchtodayorders.php",
      "Yesterday Orders": "fetchyesterdayorders.php",
      "Processing Orders": "fetchprocessingorders.php",
      "Pending Orders": "fetchpendingorders.php",
      "Shipped Orders": "fetchshippedorders.php",
      "Delivered Orders": "fetchdeliveredorders.php",
      "Canceled Orders": "fetchcanceledorders.php",
    };

    const endpoint = endpointsMap[target];
    if (!endpoint) {
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${APIURL.baseURL}/${endpoint}`);
      if (!response.ok) throw new Error("Failed to fetch data");

      const data = await response.json();
      setOrders(data.orders || []);
      message.success(`${target} loaded successfully`);
    } catch (err) {
      console.error("Error fetching orders:", err);
      message.error("Failed to fetch order details");
    } finally {
      setLoading(false);
    }
  };


  const handleRefresh = () => {
    if (selectedTable) {
      handleCardClick(selectedTable);
    }
  };

  const handleExport = () => {
    // Implement export functionality
    message.info('Export feature coming soon!');
  };

  const closeTable = () => {
    setSelectedTable(null);
    setOrders([]);
    setSearchTerm('');
    setStatusFilter('all');
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'delivered': return '#00897b';
      case 'shipped': return '#0288d1';
      case 'processing': return '#ff8f00';
      case 'pending': return '#f57c00';
      case 'canceled': return '#5f6368';
      default: return '#78909c';
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid': return '#00897b';
      case 'pending': return '#ff8f00';
      case 'failed': return '#d32f2f';
      default: return '#78909c';
    }
  };

  const getUserInitials = (username) => {
    return username
      .split(' ')
      .map(name => name[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const handleNotificationClick = () => {
    message.info('Notifications feature coming soon!');
  };

  const handleSettingsClick = () => {
    message.info('Settings feature coming soon!');
  };

  return (
    <DashboardContainer>
      {/* Header */}
      <HeaderContainer>
        <HeaderTitle>
          <DashboardIcon sx={{ fontSize: 28, color: '#00838f' }} />
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#00838f' }}>
            Order Dashboard
          </Typography>
        </HeaderTitle>

        <UserProfileHeader>
          {/* <NotificationButton onClick={handleNotificationClick}>
            <Badge count={3} size="small">
              <NotificationsIcon />
            </Badge>
          </NotificationButton>

          <NotificationButton onClick={handleSettingsClick}>
            <SettingsIcon />
          </NotificationButton> */}

          <UserInfoContainer>
            <UserAvatar>
              {getUserInitials(userData.username)}
            </UserAvatar>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '2px'
            }}>
              <span style={{
                fontSize: '14px',
                fontWeight: 600,
                color: '#00838f',
                textTransform: 'capitalize',
              }}>
                {userData.username || 'User'}
              </span>
              <span style={{
                fontSize: '12px',
                fontWeight: 500,
                color: '#00acc1',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                {userData.role}
              </span>
            </div>
          </UserInfoContainer>

          <Logout />
        </UserProfileHeader>
      </HeaderContainer>

      <ContentContainer>
        {/* Welcome Section */}
        <HeaderSection>
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Typography variant="body1" sx={{ color: '#455a64', fontSize: '14px' }}>
              Welcome back, <strong>{userData.username}</strong>! Monitor and manage your orders in real-time
            </Typography>
          </motion.div>
        </HeaderSection>

        {/* Main Stats Cards */}
        <StatsGrid>
          {[
            {
              title: 'Total Orders',
              count: totalOrderCount,
              icon: <SpeakerNotesIcon sx={{ fontSize: 24, color: '#00838f' }} />,
              accentColor: '#00838f',
              bgColor: 'linear-gradient(135deg, #e0f2f1, #b2dfdb)',
              trend: '+12%'
            },
            {
              title: 'Today Orders',
              count: todayOrderCount,
              icon: <TodayIcon sx={{ fontSize: 24, color: '#0097a7' }} />,
              accentColor: '#0097a7',
              bgColor: 'linear-gradient(135deg, #e0f7fa, #b3e5fc)',
              trend: '+8%'
            },
            {
              title: 'Yesterday Orders',
              count: yesterdayOrdercount,
              icon: <EventIcon sx={{ fontSize: 24, color: '#00acc1' }} />,
              accentColor: '#00acc1',
              bgColor: 'linear-gradient(135deg, #e1f5fe, #b3e5fc)',
              trend: '+5%'
            }
          ].map((stat, index) => (
            <StyledCard
              key={index}
              accentColor={stat.accentColor}
              variants={cardVariants}
              initial="hidden"
              animate="visible"
              whileHover="hover"
              transition={{ duration: 0.3, delay: index * 0.1 }}
              onClick={() => handleCardClick(stat.title)}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, color: '#263238' }}>
                    {stat.count.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#546e7a', fontWeight: 500, fontSize: '13px' }}>
                    {stat.title}
                  </Typography>
                </Box>
                <IconContainer style={{ background: stat.bgColor }}>
                  {stat.icon}
                </IconContainer>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1}>
                <TrendingUpIcon sx={{ fontSize: 14, color: '#00897b' }} />
                <Typography variant="caption" sx={{ color: '#00897b', fontWeight: 500, fontSize: '11px' }}>
                  {stat.trend} from last month
                </Typography>
              </Stack>
            </StyledCard>
          ))}
        </StatsGrid>

        {/* Status Cards */}
        <StatusGrid>
          {[
            {
              label: "Processing",
              count: processingOrderCount,
              icon: <AutorenewIcon sx={{ fontSize: 20, color: '#ff8f00' }} />,
              color: '#ff8f00',
              bgColor: 'linear-gradient(135deg, #fff3e0, #ffe0b2)'
            },
            {
              label: "Shipped",
              count: shippedOrderCount,
              icon: <LocalShippingIcon sx={{ fontSize: 20, color: '#0288d1' }} />,
              color: '#0288d1',
              bgColor: 'linear-gradient(135deg, #e3f2fd, #bbdefb)'
            },
            {
              label: "Delivered",
              count: deliveredOrderCount,
              icon: <HouseIcon sx={{ fontSize: 20, color: '#00897b' }} />,
              color: '#00897b',
              bgColor: 'linear-gradient(135deg, #e0f2f1, #b2dfdb)'
            },
            {
              label: "Canceled",
              count: canceledOrderCount,
              icon: <RemoveShoppingCartOutlinedIcon sx={{ fontSize: 20, color: '#757575' }} />,
              color: '#757575',
              bgColor: 'linear-gradient(135deg, #fafafa, #f5f5f5)'
            }
          ].map((item, index) => (
            <StatusCard
              key={index}
              accentColor={item.color}
              variants={cardVariants}
              initial="hidden"
              animate="visible"
              whileHover="hover"
              transition={{ duration: 0.3, delay: 0.3 + index * 0.1 }}
              onClick={() => handleCardClick(`${item.label} Orders`)}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
                <IconContainer style={{ background: item.bgColor }}>
                  {item.icon}
                </IconContainer>
                <Typography variant="h5" sx={{ fontWeight: 600, color: '#263238' }}>
                  {item.count}
                </Typography>
              </Stack>
              <Typography variant="body2" sx={{ color: '#546e7a', fontWeight: 500, fontSize: '12px' }}>
                {item.label}
              </Typography>
            </StatusCard>
          ))}
        </StatusGrid>

        {/* Enhanced Table Section */}
        <AnimatePresence>
          {selectedTable && (
            <TableContainer
              variants={tableVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              transition={{ duration: 0.4 }}
            >
              <TableHeader>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Badge count={filteredOrders.length} showZero color="#00838f">
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#263238' }}>
                      {selectedTable} Details
                    </Typography>
                  </Badge>
                  <ActionButton
                    icon={<CloseIcon />}
                    onClick={closeTable}
                    type="text"
                    danger
                  />
                </Stack>

                <SearchContainer>
                  <Input
                    placeholder="Search orders..."
                    prefix={<SearchIcon />}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                      width: 250,
                      borderRadius: '8px',
                      background: 'rgba(255, 255, 255, 0.8)',
                      backdropFilter: 'blur(5px)',
                      border: '1px solid rgba(0, 188, 212, 0.2)'
                    }}
                  />
                  <Select
                    value={statusFilter}
                    onChange={setStatusFilter}
                    style={{ width: 150 }}
                    placeholder="Filter by status"
                    suffixIcon={<FilterListIcon />}
                  >
                    <Option value="all">All Status</Option>
                    <Option value="pending">Pending</Option>
                    <Option value="processing">Processing</Option>
                    <Option value="shipped">Shipped</Option>
                    <Option value="delivered">Delivered</Option>
                    <Option value="canceled">Canceled</Option>
                  </Select>
                  <ActionButton
                    icon={<RefreshIcon />}
                    onClick={handleRefresh}
                    type="primary"
                  />
                  {/* <ActionButton 
                    icon={<GetAppIcon />} 
                    onClick={handleExport}
                    type="default"
                  >
                    Export
                  </ActionButton> */}
                </SearchContainer>
              </TableHeader>

              {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  >
                    <RefreshIcon sx={{ fontSize: 32, color: '#00838f' }} />
                  </motion.div>
                </Box>
              ) : (
                <div className="overflow-x-auto">
                  {filteredOrders.length === 0 ? (
                    <Box textAlign="center" py={8}>
                      <Typography variant="h6" color="textSecondary">
                        No orders found
                      </Typography>
                      <Typography variant="body2" color="textSecondary" mt={1}>
                        Try adjusting your search or filters
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      <div style={{
                        borderRadius: '12px',
                        overflow: 'hidden',
                        border: '1px solid rgba(0, 188, 212, 0.1)',
                        background: 'rgba(255, 255, 255, 0.8)',
                        backdropFilter: 'blur(5px)'
                      }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead style={{ background: 'linear-gradient(135deg, #f1f5f9, #e2e8f0)' }}>
                            <tr>
                              {['S.No', 'Order ID', 'Name', 'Amount', 'Payment Status', 'Order Status', 'Payment Method', 'Delivery Address'].map((header) => (
                                <th key={header} style={{
                                  padding: '12px 16px',
                                  textAlign: 'left',
                                  fontWeight: 600,
                                  color: '#263238',
                                  borderBottom: '1px solid rgba(0, 188, 212, 0.1)',
                                  fontSize: '13px'
                                }}>
                                  {header}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {currentOrders.map((order, index) => (
                              <motion.tr
                                key={order.id}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: index * 0.05 }}
                                style={{
                                  borderBottom: '1px solid rgba(0, 188, 212, 0.08)',
                                  transition: 'background-color 0.2s ease'
                                }}
                                onMouseEnter={(e) => e.target.closest('tr').style.backgroundColor = 'rgba(0, 188, 212, 0.03)'}
                                onMouseLeave={(e) => e.target.closest('tr').style.backgroundColor = 'transparent'}
                              >
                                <td style={{ padding: '12px 16px', color: '#546e7a', fontSize: '13px' }}>
                                  {(currentPage - 1) * itemsPerPage + index + 1}
                                </td>
                                <td style={{ padding: '12px 16px', fontWeight: 500, color: '#263238', fontSize: '13px' }}>
                                  {order.order_id || '__'}
                                </td>
                                <td style={{ padding: '12px 16px', color: '#263238', fontSize: '13px' }}>
                                  {order.user_name || '__'}
                                </td>
                                <td style={{ padding: '12px 16px', fontWeight: 600, color: '#263238', fontSize: '13px' }}>
                                  ₹{order.order_total || '__'}
                                </td>
                                <td style={{ padding: '12px 16px' }}>
                                  <span style={{
                                    padding: '3px 8px',
                                    borderRadius: '12px',
                                    fontSize: '11px',
                                    fontWeight: 500,
                                    backgroundColor: getPaymentStatusColor(order.payment_status) + '20',
                                    color: getPaymentStatusColor(order.payment_status),
                                    border: `1px solid ${getPaymentStatusColor(order.payment_status)}30`
                                  }}>
                                    {order.payment_status || '__'}
                                  </span>
                                </td>
                                <td style={{ padding: '12px 16px' }}>
                                  <span style={{
                                    padding: '3px 8px',
                                    borderRadius: '12px',
                                    fontSize: '11px',
                                    fontWeight: 500,
                                    backgroundColor: getStatusColor(order.order_status) + '20',
                                    color: getStatusColor(order.order_status),
                                    border: `1px solid ${getStatusColor(order.order_status)}30`
                                  }}>
                                    {order.order_status || '__'}
                                  </span>
                                </td>
                                <td style={{ padding: '12px 16px', color: '#263238', fontSize: '13px' }}>
                                  {order.payment_method || '__'}
                                </td>
                                <td style={{ padding: '12px 16px', color: '#546e7a', maxWidth: '200px', fontSize: '13px' }}>
                                  <div style={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}>
                                    {order.address || '__'}
                                  </div>
                                </td>
                              </motion.tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Enhanced Pagination */}
                      {filteredOrders.length > 0 && (
                        <Box mt={3} p={2} sx={{
                          background: 'linear-gradient(135deg, #f1f5f9, #e2e8f0)',
                          borderRadius: '12px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          gap: 2,
                          backdropFilter: 'blur(5px)',
                          border: '1px solid rgba(0, 188, 212, 0.1)'
                        }}>
                          <Stack direction="row" alignItems="center" spacing={2}>
                            <Typography variant="body2" color="textSecondary" sx={{ fontSize: '13px' }}>
                              Rows per page:
                            </Typography>
                            <Select
                              value={itemsPerPage}
                              onChange={(value) => {
                                setCurrentPage(1);
                                setItemsPerPage(value);
                              }}
                              style={{ width: 80 }}
                              size="small"
                            >
                              <Option value={10}>10</Option>
                              <Option value={50}>50</Option>
                              <Option value={100}>100</Option>
                            </Select>
                          </Stack>

                          <Typography variant="body2" color="textSecondary" sx={{ fontSize: '13px' }}>
                            Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, totalItems)} of {totalItems} entries
                          </Typography>

                          <Stack direction="row" spacing={1}>
                            <ActionButton
                              onClick={() => setCurrentPage(1)}
                              disabled={currentPage === 1}
                              size="small"
                            >
                              First
                            </ActionButton>
                            <ActionButton
                              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                              disabled={currentPage === 1}
                              size="small"
                            >
                              Previous
                            </ActionButton>

                            {[...Array(Math.min(5, totalPages))].map((_, i) => {
                              const page = currentPage - 2 + i;
                              if (page > 0 && page <= totalPages) {
                                return (
                                  <ActionButton
                                    key={page}
                                    onClick={() => setCurrentPage(page)}
                                    type={currentPage === page ? "primary" : "default"}
                                    size="small"
                                  >
                                    {page}
                                  </ActionButton>
                                );
                              }
                              return null;
                            })}

                            <ActionButton
                              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                              disabled={currentPage === totalPages}
                              size="small"
                            >
                              Next
                            </ActionButton>
                            <ActionButton
                              onClick={() => setCurrentPage(totalPages)}
                              disabled={currentPage === totalPages}
                              size="small"
                            >
                              Last
                            </ActionButton>
                          </Stack>
                        </Box>
                      )}
                    </>
                  )}
                </div>
              )}
            </TableContainer>
          )}
        </AnimatePresence>
      </ContentContainer>
    </DashboardContainer>
  );
};

export default Dashboard;
